import { useCallback, useState } from 'react';
import { useLocation } from 'wouter';

import { useServices } from '@/shared/hooks/use-services';
import { queryClient } from '@/shared/lib/query-client';
import { enhancedToast } from '@/shared/utils/enhanced-toast';
import { UIError } from '@/shared/utils/ui-error';
import { useTracking } from '@/modules/analytics/hooks/use-tracking';

import type { ClubCatalogItem } from '../types/club';
import { formatPoints } from '../utils/points';

/** Chave do sessionStorage que liga o resgate à tela do produto (complementos). */
export const CLUB_REDEEM_STORAGE_KEY = 'club-redeem';

export interface ClubRedeemContext {
  slug: string;
  product_multiplier: number;
  available_points: number;
}

export function readClubRedeemContext(): ClubRedeemContext | null {
  try {
    const raw = sessionStorage.getItem(CLUB_REDEEM_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ClubRedeemContext) : null;
  } catch {
    return null;
  }
}

export function clearClubRedeemContext() {
  sessionStorage.removeItem(CLUB_REDEEM_STORAGE_KEY);
}

interface UseClubRedeemInput {
  slug: string;
  productMultiplier: number;
  availablePoints: number;
}

/**
 * Resgate de um produto do catálogo do Clube: o item entra no carrinho de
 * sempre, marcado como resgate e com o custo em pontos. Produto que exige
 * complemento/combo abre a tela do produto em modo resgate.
 */
export function useClubRedeem({ slug, productMultiplier, availablePoints }: UseClubRedeemInput) {
  const [, navigate] = useLocation();
  const { addItem, clearCart } = useServices();
  const { loyaltyProductRedeem } = useTracking();
  const [pendingItem, setPendingItem] = useState<ClubCatalogItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canAfford = useCallback(
    (item: ClubCatalogItem) => availablePoints >= item.points_required,
    [availablePoints]
  );

  const openRedeem = useCallback(
    (item: ClubCatalogItem) => {
      if (!canAfford(item)) {
        enhancedToast.info('Ainda faltam pontos', {
          description: `Você precisa de ${formatPoints(item.points_required - availablePoints)} a mais para trocar ${item.name}.`,
        });
        return;
      }

      if (item.requires_customization) {
        // A tela do produto monta o item com os complementos e fecha o resgate.
        sessionStorage.setItem(
          CLUB_REDEEM_STORAGE_KEY,
          JSON.stringify({
            slug,
            product_multiplier: productMultiplier,
            available_points: availablePoints,
          } satisfies ClubRedeemContext)
        );
        navigate(`/${slug}?editProduct=${item.id}&clube=1`);
        return;
      }

      setPendingItem(item);
    },
    [availablePoints, canAfford, navigate, productMultiplier, slug]
  );

  const closeRedeem = useCallback(() => {
    if (!isSubmitting) setPendingItem(null);
  }, [isSubmitting]);

  const addRewardToCart = useCallback(
    async (item: ClubCatalogItem) => {
      await addItem.execute({
        item_id: item.id,
        name: item.name,
        price: item.effective_price,
        total_price: item.effective_price,
        quantity: 1,
        store_slug: slug,
        observation: '',
        image_id: item.image,
        addons: [],
        is_reward: true,
        loyalty_points_cost: item.points_required,
      });
    },
    [addItem, slug]
  );

  const confirmRedeem = useCallback(async () => {
    if (!pendingItem) return;
    setIsSubmitting(true);

    try {
      try {
        await addRewardToCart(pendingItem);
      } catch (error) {
        if (error instanceof UIError && error.code === 'ITEMS_WITH_DIFFERENT_STORE') {
          const shouldClear = window.confirm(
            `${error.message} Quer esvaziar o carrinho para trocar este produto?`
          );
          if (!shouldClear) return;
          await clearCart.execute();
          await addRewardToCart(pendingItem);
        } else {
          throw error;
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['cart-items'] });
      loyaltyProductRedeem(slug, pendingItem.id, pendingItem.points_required);
      enhancedToast.success('Produto adicionado ao carrinho', {
        description: `${pendingItem.name} por ${formatPoints(pendingItem.points_required)}.`,
      });
      setPendingItem(null);
      navigate('/cart/review-order');
    } catch {
      enhancedToast.error('Não foi possível adicionar o produto ao carrinho');
    } finally {
      setIsSubmitting(false);
    }
  }, [addRewardToCart, clearCart, loyaltyProductRedeem, navigate, pendingItem, slug]);

  return { pendingItem, isSubmitting, canAfford, openRedeem, closeRedeem, confirmRedeem };
}
